import * as Api from '@ViewModels';
import { useQueryClient } from '@tanstack/react-query';
import { css } from 'aphrodite';
import moment from 'moment';
import { ReactNode, useEffect, useId, useRef, useState } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTabActivityIndicator } from '../../../../../hooks/useTabActivityIndicator';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { invalidateAllContentCalendarSuggestions, useAdminAccountQuery } from '../../../../../queries';
import { useAccountProfileQuery } from '../../../../../queries/AccountProfile/useAccountProfileQuery';
import { useBrandingSettingsQuery } from '../../../../../queries/AccountProfile/useBrandingSettingsQuery';
import { useAIAssistantCreateConversationMutation } from '../../../../../queries/AI/useAIAssistantCreateConversationMutation';
import {
	getConversationChatMessageQueryKey,
	useAIConversationChatMessageQuery,
} from '../../../../../queries/AIConversation/useAIConversationChatMessageQuery';
import { invalidateAllAIConversationFilterQueries } from '../../../../../queries/AIConversation/useAIConversationFilterQuery';
import { useApproveSuggestedContentMutation } from '../../../../../queries/ContentCalendarSuggestion/useApproveSuggestedContentMutation';
import { useApproveSuggestedContentPreviewMutation } from '../../../../../queries/ContentCalendarSuggestion/useApproveSuggestedContentPreviewMutation';

import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../components/ConfirmationDialog';
import { WarningIcon, WarningIconType } from '../../../../components/svgs/icons/WarningIcon';
import { AIConversationMessage } from '../../../../components-v2/ai/AIConversationMessage';
import {
	ChatConversation,
	ChatConversationContent,
	ChatConversationScrollButton,
} from '../../../../components-v2/ai/ChatConversation';
import { ThinkingIndicator } from '../../../../components-v2/ai/ThinkingIndicator';
import { Button } from '../../../../components-v2/Button';
import { AiGeneratedContentRefineDialog } from '../../../ContentCalendar/AiGeneratedContentRefineDialog';
import { bs } from '../../../../styles/styles';
import { dispatchContentCalendarAccepted } from '../../../ContentCalendar/events';
import {
	AIChatAssistantMessage,
	AssistantChatInput,
	removeErrorMessageFromConversationCache,
	UniversalAssistantLayout,
	useAIAssistantStreamingMutation,
} from '../';
import { s } from '../styles';
import { ContentCalendarGenerationForm } from './ContentCalendarGenerationForm';
import { styleSheet } from './styles';
import { hasBrandingSettings } from '../../../../../utils/hasBrandingSettings';

const CONTENT_CALENDAR_ASSISTANT_NAME = 'ContentCalendarAgent';

interface ContentCalendarAssistantChatProps {
	impersonationContext: Api.IImpersonationContext;
	initialConversationId?: string;
	onUseAIEmail?: (subject: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseSocialContent?: (content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseGoogleReviewReply?: (reply: string, sessionId?: string) => void;
	onUseBlogContent?: (subject: string, content: Api.IRawRichTextContentState, aiReference?: Api.IAIReference) => void;
	onUseContact?: (value: {
		firstName?: string;
		lastName?: string;
		email?: string;
		phoneNumber?: string;
		company?: string;
	}) => void;
	onClose?: () => void;
	renderConversationStarters?: (
		conversationId: string,
		streamingMutation: ReturnType<typeof useAIAssistantStreamingMutation>
	) => ReactNode;
}

const ContentCalendarAssistantChatErrorBoundary = ({
	children,
	impersonationContext,
}: {
	children: ReactNode;
	impersonationContext: Api.IImpersonationContext;
}) => {
	const logger = useEventLogging('AssistantChatErrorBoundary');
	const errorMessages = useErrorMessages();
	const { mutate: createUniversalAssistantConversation } = useAIAssistantCreateConversationMutation({
		onSuccess: () => {
			invalidateAllAIConversationFilterQueries();
		},
		onError: error => {
			errorMessages.pushApiError(error);
			logger.logApiError(`Create-AdminAgent-Assistant-Error`, error);
		},
	});
	const handleNewChatClick = () => {
		createUniversalAssistantConversation({
			assistantName: CONTENT_CALENDAR_ASSISTANT_NAME,
			impersonationContext,
		});
	};

	return (
		<ErrorBoundary
			fallback={
				<UniversalAssistantLayout onNewChat={handleNewChatClick}>
					<div className={css(bs.flex, bs.flexCol, bs.itemsCenter, bs.justifyCenter, bs.p2, bs.hFull)}>
						<WarningIcon />
						<div className={css(bs.fontBold, bs.textBrandPrimary, bs.mt4)}>Error occurred loading your chat</div>
						<Button variant='secondary' styles={[bs.mt4]} onClick={handleNewChatClick}>
							Start new chat
						</Button>
					</div>
				</UniversalAssistantLayout>
			}
			onError={(error: Error) => {
				logger.logApiError('AssistantChatErrorBoundary-Error', error as Api.IOperationResultNoValue);
			}}
		>
			{children}
		</ErrorBoundary>
	);
};

export function ContentCalendarAssistantChat(props: ContentCalendarAssistantChatProps) {
	return (
		<ContentCalendarAssistantChatErrorBoundary impersonationContext={props.impersonationContext}>
			<ContentCalendarAssistantChartInstance {...props} />
		</ContentCalendarAssistantChatErrorBoundary>
	);
}

const ContentCalendarGenerationPreviewConfirmationOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: false,
		isCta: true,
		title: 'Replace AI Suggestions',
	},
	{
		isCancel: true,
		isCta: false,
		title: 'Cancel',
	},
];

export function ContentCalendarAssistantChartInstance({
	onClose,
	renderConversationStarters,
	impersonationContext,
	initialConversationId,
}: ContentCalendarAssistantChatProps) {
	const id = useId();
	const [activeConversationId, setActiveConversationId] = useState<string | null>(null);

	const userSession = useUserSession();
	const queryClient = useQueryClient();
	const errorMessages = useErrorMessages();
	const logger = useEventLogging('ContentCalendarAssistantChat');
	const accountQuery = useAdminAccountQuery({
		accountId: impersonationContext.account.id,
	});
	const accountProfileQuery = useAccountProfileQuery({ impersonationContext });
	const contentProfile = accountProfileQuery.data;
	const brandingSettingsQuery = useBrandingSettingsQuery({ impersonationContext });
	// Keep this indeterminate (undefined) while the query is in flight so the form does not flash a
	// misleading "set up branding" hint to accounts that actually have branding configured.
	const accountHasBrandingSettings = brandingSettingsQuery.isLoading
		? undefined
		: hasBrandingSettings(brandingSettingsQuery.data);

	const { mutateAsync: createConversationAsync, isPending: isCreatingConversation } =
		useAIAssistantCreateConversationMutation({
			onSuccess: () => {
				invalidateAllAIConversationFilterQueries();
			},
			onError: error => {
				errorMessages.pushApiError(error);
				logger.logApiError(`Create-AdminAgent-Assistant-Error`, error);
			},
		});

	const [showApproveContentCalendarModal, setShowApproveContentCalendarModal] = useState(false);
	const [generateContentToolCall, setGenerateContentToolCall] = useState<Api.ContentCalendarResourceToolCall | null>(
		null
	);
	// Approval is additive — remember which entries were already scheduled so their cards render read-only
	const [approvedEntryIds, setApprovedEntryIds] = useState<string[]>([]);
	const [previewEntry, setPreviewEntry] = useState<Api.ContentCalendarEntryToolCall | null>(null);

	useEffect(() => {
		if (initialConversationId) {
			setActiveConversationId(initialConversationId);
			setApprovedEntryIds([]);
			setGenerateContentToolCall(null);
			setPreviewEntry(null);
			setShowApproveContentCalendarModal(false);
		}
	}, [initialConversationId]);

	const handleNewChatClick = () => {
		setActiveConversationId(null);
		setApprovedEntryIds([]);
		setGenerateContentToolCall(null);
		setPreviewEntry(null);
		setShowApproveContentCalendarModal(false);
	};

	const { mutate: approveSuggestedContent, isPending: isApprovingSuggestedContent } =
		useApproveSuggestedContentMutation({
			onSuccess: (_data, variables) => {
				const newlyApproved = variables.suggestionIds ?? [];
				if (newlyApproved.length) {
					setApprovedEntryIds(prev => [...prev, ...newlyApproved.filter(entryId => !prev.includes(entryId))]);
				}
				invalidateAllContentCalendarSuggestions();
				setGenerateContentToolCall(null);
				dispatchContentCalendarAccepted(variables.calendarId);
			},
			onError: error => {
				errorMessages.pushApiError(error);
				logger.logApiError(`Approve-ContentCalendar-Error`, error);
			},
		});

	const { mutate: approveSuggestedContentPreview, isPending: isApprovingSuggestedContentPreview } =
		useApproveSuggestedContentPreviewMutation({
			onSuccess: data => {
				if (data?.values?.length > 0) {
					setShowApproveContentCalendarModal(true);
					return;
				}

				setShowApproveContentCalendarModal(false);

				if (!generateContentToolCall) {
					return;
				}

				handleApproveContentCalendar(generateContentToolCall);
			},
			onError: error => {
				errorMessages.pushApiError(error);
				logger.logApiError(`Approve-ContentCalendar-Preview-Error`, error);
			},
		});

	const handleApproveContentCalendarPreview = (
		toolCall: Api.ContentCalendarResourceToolCall,
		selectedEntryIds?: string[]
	) => {
		if (selectedEntryIds !== undefined) {
			approveSuggestedContent({
				calendarId: toolCall.calendarId,
				contentCalendarSuggestion: toolCall,
				impersonationContext,
				suggestionIds: selectedEntryIds,
			});
			return;
		}

		setGenerateContentToolCall(toolCall);
		approveSuggestedContentPreview({
			calendarId: toolCall.calendarId,
			impersonationContext,
		});
	};

	const handleApproveContentCalendar = (toolCall: Api.ContentCalendarResourceToolCall) => {
		approveSuggestedContent({
			calendarId: toolCall.calendarId,
			contentCalendarSuggestion: toolCall,
			impersonationContext,
		});
	};

	const handlePreviewCalendarEntry = (entry: Api.ContentCalendarEntryToolCall) => {
		setPreviewEntry(entry);
	};

	const onRequestClose = (result?: IConfirmationDialogOption<boolean>, canceled?: boolean) => {
		if (canceled) {
			setShowApproveContentCalendarModal(false);
			setGenerateContentToolCall(null);
			return;
		}

		setShowApproveContentCalendarModal(false);

		if (!generateContentToolCall) {
			return;
		}

		handleApproveContentCalendar(generateContentToolCall);
	};

	const aiConversationChatMessageQuery = useAIConversationChatMessageQuery({
		enabled: Boolean(activeConversationId),
		id: activeConversationId,
		staleTime: Infinity,
		impersonationContext,
	});

	const impersonationContextRef = useRef(impersonationContext);
	impersonationContextRef.current = impersonationContext;

	const aiAssistantStreamingMutation = useAIAssistantStreamingMutation({
		assistantName: CONTENT_CALENDAR_ASSISTANT_NAME,
	});

	useTabActivityIndicator(aiAssistantStreamingMutation.status);

	const handleTryAgain = (chatRequest: Api.AIChatRequest) => {
		if (!activeConversationId) return;
		removeErrorMessageFromConversationCache(activeConversationId);
		aiAssistantStreamingMutation.mutate({
			conversationId: activeConversationId,
			chatRequest: { ...chatRequest, includeProgress: true },
			impersonationContext,
		});
	};

	const handleGenerate = async (contentTypes: string[], instructions: string, customizeExistingContent: boolean) => {
		const startDate = moment();
		const endDate = moment().add(90, 'days');

		let conversationId = activeConversationId;
		if (!conversationId) {
			try {
				const conversation = await createConversationAsync({
					assistantName: CONTENT_CALENDAR_ASSISTANT_NAME,
					impersonationContext: impersonationContextRef.current,
				});
				setActiveConversationId(conversation.id);
				conversationId = conversation.id;
			} catch {
				return;
			}
		}

		aiAssistantStreamingMutation.mutate({
			conversationId,
			chatRequest: {
				_type: 'AIActionRequest',
				includeProgress: true,
				action: {
					_type: 'ContentCalendarGenerationAction',
					startDate: startDate.format('YYYY-MM-DD'),
					endDate: endDate.format('YYYY-MM-DD'),
					contentTypes: contentTypes.length > 0 ? contentTypes : undefined,
					instructions: instructions.trim() || undefined,
					customizeExistingContent,
					supportsIndividualScheduling: true,
				},
			} as Api.AIMessageRequest,
			impersonationContext,
		});
	};

	const handlePromptSubmit = (prompt: string) => {
		if (!activeConversationId) return;
		removeErrorMessageFromConversationCache(activeConversationId);
		queryClient.setQueryData(
			getConversationChatMessageQueryKey({
				id: activeConversationId,
				impersonationContext,
			}),
			(prevMessages: Api.AIChatMessage[]) => [
				...(prevMessages ?? []),
				{ role: Api.AIChatRole.User, text: prompt, id: null },
			]
		);
		aiAssistantStreamingMutation.mutate({
			conversationId: activeConversationId,
			chatRequest: {
				includeProgress: true,
				chatMessages: [
					{
						role: Api.AIChatRole.User,
						text: prompt,
						id: null,
					},
				],
			} as Api.AIMessageRequest,
			impersonationContext,
		});
	};

	return (
		<UniversalAssistantLayout onClose={onClose} onNewChat={handleNewChatClick}>
			<ChatConversation>
				<ChatConversationContent>
					<AIChatAssistantMessage>
						<div className={css(styleSheet.assistantDefaultMessageContent)}>
							<span>
								Hi {userSession.user.firstName}! What content would you like planned for{' '}
								{`${accountQuery.data?.companyName ?? 'this account'}`}?
							</span>

							<ContentCalendarGenerationForm
								onGenerate={handleGenerate}
								disabled={
									isCreatingConversation ||
									aiAssistantStreamingMutation.isLoading ||
									aiAssistantStreamingMutation.isStreaming
								}
								contentProfile={contentProfile}
								accountFeatures={impersonationContext.account.features}
								accountHasBrandingSettings={accountHasBrandingSettings}
							/>
						</div>
					</AIChatAssistantMessage>

					{renderConversationStarters &&
					activeConversationId &&
					!aiConversationChatMessageQuery.data?.length &&
					!aiAssistantStreamingMutation.isLoading &&
					!aiAssistantStreamingMutation.isStreaming
						? renderConversationStarters(activeConversationId, aiAssistantStreamingMutation)
						: null}
					{aiConversationChatMessageQuery.data?.map((message, i) => {
						return (
							<AIConversationMessage
								key={`${id}-message-${message.id ?? i}`}
								message={message}
								onErrorTryAgain={handleTryAgain}
								onApproveContentCalendar={handleApproveContentCalendarPreview}
								conversationId={activeConversationId}
								onApproveContentCalendarLoading={isApprovingSuggestedContent || isApprovingSuggestedContentPreview}
								approvedContentCalendarEntryIds={approvedEntryIds}
								onPreviewCalendarEntry={handlePreviewCalendarEntry}
							/>
						);
					})}
					{isCreatingConversation || aiAssistantStreamingMutation.isLoading ? (
						<AIChatAssistantMessage isThinking>
							<ThinkingIndicator showTime={false} />
						</AIChatAssistantMessage>
					) : null}
				</ChatConversationContent>
				<ChatConversationScrollButton />
			</ChatConversation>
			<div className={css(s.inputContainer)}>
				<AssistantChatInput
					streamingInProgress={aiAssistantStreamingMutation.isLoading || aiAssistantStreamingMutation.isStreaming}
					placeholder='Send a message...'
					onSubmit={handlePromptSubmit}
				/>
			</div>

			{previewEntry && (
				<AiGeneratedContentRefineDialog
					entry={previewEntry}
					open={!!previewEntry}
					onOpenChange={open => !open && setPreviewEntry(null)}
					impersonationContext={impersonationContext}
				/>
			)}
			<ConfirmationDialog
				icon={<WarningIcon type={WarningIconType.Blue} />}
				modalProps={{
					isOpen: showApproveContentCalendarModal,
					onRequestClose,
				}}
				options={ContentCalendarGenerationPreviewConfirmationOptions}
				title={
					<div>
						<div>Warning</div>

						<p className={css(styleSheet.contentCalendarGenerationPreviewConfirmationText)}>
							This date range already has AI-generated <br /> suggestions. Continuing will replace those <br />{' '}
							suggestions with new ones. <br /> Already-scheduled content will not be affected.
						</p>
					</div>
				}
			/>
		</UniversalAssistantLayout>
	);
}
